/* ==========================================================================
   NZ Supps — Gift Popup ("Pick a card")
   Drives sections/nzs-gift-popup.liquid.

   Flow:  deal in 9 cards (optional sneak peek of the faces) -> customer
          flips N cards -> email step -> claim
          -> gifts POST to /cart/add.js, thumbnails fly into the cart icon,
             the theme's cart drawer opens (#nzsCartToggle).

   All config arrives on data-* attributes written by the section, so the
   theme editor is the control panel. No dependencies (no Alpine, no jQuery).
   ========================================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'nzsGiftPopup.v1';
  var CART_TOGGLES = ['#nzsCartToggle', '[data-nzs-cart-toggle]', '.js-cart-toggle'];
  var instances = [];
  var cssInjected = false;

  /* NZS-SPD-0820: the section ships no stylesheet link — the CSS is fetched only when a
     surface actually needs it (open(), or showTab() for the docked re-open pill, which
     is styled by the same file and can appear without open() ever running).
     ponytail: open()'s `position !== 'fixed'` check + deferOpen() ARE the load-wait —
     the first open() starts the fetch and the retry lands once the CSS applies. */
  function injectCss(el) {
    if (cssInjected) return;
    var href = el && el.getAttribute('data-css');
    if (!href) return;
    cssInjected = true;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  /* ------------------------------ helpers ------------------------------ */
  function qs(el, sel) { return el.querySelector(sel); }
  function qsa(el, sel) { return Array.prototype.slice.call(el.querySelectorAll(sel)); }
  function on(el, ev, fn) { if (el) el.addEventListener(ev, fn); }
  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function designMode() { return !!(window.Shopify && window.Shopify.designMode); }
  function root_() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  function param(name) {
    try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
  }
  function forced() { return param('nzs_gift') === '1' || param('nzs_gift_dev') === '1'; }
  function readStore() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); } catch (e) { return {}; }
  }
  function writeStore(patch) {
    try {
      var s = readStore();
      for (var k in patch) if (Object.prototype.hasOwnProperty.call(patch, k)) s[k] = patch[k];
      localStorage.setItem(STORE_KEY, JSON.stringify(s));
    } catch (e) { /* private mode — popup just re-shows, no harm */ }
  }
  function money(cents, symbol) {
    return (symbol || '$') + (Math.round(cents) / 100).toFixed(2);
  }
  function cartButton() {
    for (var i = 0; i < CART_TOGGLES.length; i++) {
      var el = document.querySelector(CART_TOGGLES[i]);
      if (el) return el;
    }
    return null;
  }

  /* ------------------------------ instance ----------------------------- */
  function GiftPopup(el) {
    this.el = el;
    this.cfg = {
      picks: Math.max(1, parseInt(el.getAttribute('data-picks'), 10) || 3),
      trigger: el.getAttribute('data-trigger') || 'delay',
      delay: (parseFloat(el.getAttribute('data-delay')) || 3) * 1000,
      frequency: el.getAttribute('data-frequency') || 'session',
      days: parseFloat(el.getAttribute('data-days')) || 7,
      addToCart: el.getAttribute('data-add-to-cart') !== 'false',
      discount: (el.getAttribute('data-discount') || '').trim(),
      showTab: el.getAttribute('data-tab') === 'true',
      symbol: el.getAttribute('data-currency-symbol') || '$',
      klaviyoKey: (el.getAttribute('data-klaviyo-key') || '').trim(),
      klaviyoList: (el.getAttribute('data-klaviyo-list') || '').trim(),
      klaviyoSource: (el.getAttribute('data-klaviyo-source') || '').trim(),
      shopifyCustomer: el.getAttribute('data-shopify-customer') !== 'false',
      peek: el.getAttribute('data-peek') !== 'false'
    };

    /* NZS-SPD2-0906-T1: the body nodes live in <template data-nzs-gp-tpl> until the first open().
       Everything the constructor used to query in here moved, verbatim, into hydrate() below. */
    this.cards = [];
    this.slots = [];
    this.claimSlots = [];
    this.claimHover = null;
    this._hydrated = false;

    this.tab = document.querySelector('[data-nzs-gp-tab]');
    this.dock = document.querySelector('[data-nzs-gp-dock]');
    this.dismissBtn = document.querySelector('[data-nzs-gp-dismiss]');
    this.scrim = qs(el, '[data-gp-scrim]');

    this.picked = [];
    this.phase = 'idle';
    this.advance = null;
    this.openedOnce = false;

    this.bind();
    this.schedule();
  }

  GiftPopup.prototype.bind = function () {
    var self = this;

    /* NZS-SPD2-0906-T1: the card / close / reset handlers need body nodes, so they moved to
       bindBody(), which hydrate() calls. Everything left here binds nodes outside the template. */
    // ponytail: reuse the scrim lookup already owned by this instance.
    on(this.scrim, 'click', function () { self.close(); });
    on(this.tab, 'click', function () { self.open(true); });
    on(this.dismissBtn, 'click', function (e) {
      e.stopPropagation();
      writeStore({ dismissed: true, dismissedAt: Date.now() });
      self.hideTab();
    });

    this._esc = function (e) { if (e.key === 'Escape' && self.phase !== 'idle') self.close(); };
    document.addEventListener('keydown', this._esc);
  };

  /* NZS-SPD2-0906-T1: clone the popup body into place, then run the constructor's original body work
     against it. Called from open() once the CSS-loaded gate has passed, so the nodes land into an
     already-styled document. Idempotent; tolerates a missing <template> (a theme-editor re-render
     ships a fresh one, and an already-cloned body just gets queried where it stands). */
  GiftPopup.prototype.hydrate = function () {
    if (this._hydrated) return;
    this._hydrated = true;
    var el = this.el;
    var tpl = qs(el, 'template[data-nzs-gp-tpl]');
    /* replace IN PLACE so the body keeps its exact position after the scrim - DOM order unchanged. */
    if (tpl && tpl.content) tpl.parentNode.replaceChild(tpl.content.cloneNode(true), tpl);

    this.cards = qsa(el, '.nzs-gp__card');

    /* RANDOM-N: draw a random subset of cards for this page load only (no
       persistence — the pool is re-drawn fresh every time init() runs).
       Fisher-Yates shuffle a COPY, keep the first showCount, bench the rest
       so they can never be flipped, tabbed to, or picked. Every other method
       below reads from this.cards (or a local alias of it), so reducing the
       array here — before bind() wires up click handlers — is enough to keep
       benched cards out of the flip/render/reset code paths. */
    var showCount = Math.max(1, parseInt(el.getAttribute('data-show-count'), 10) || 9);
    if (this.cards.length > showCount) {
      var pool = this.cards.slice();
      for (var si = pool.length - 1; si > 0; si--) {
        var sj = Math.floor(Math.random() * (si + 1));
        var stmp = pool[si]; pool[si] = pool[sj]; pool[sj] = stmp;
      }
      var bench = pool.slice(showCount);
      bench.forEach(function (c) {
        c.classList.add('nzs-gp__card--bench');
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('tabindex', '-1');   // the card itself is the <button>
      });
      this.cards = pool.slice(0, showCount);
    }
    this.cfg.picks = Math.min(this.cfg.picks, this.cards.length);

    this.grid = qs(el, '.nzs-gp__grid');
    this.slots = qsa(el, '[data-gp-slots="pick"] .nzs-gp__slot');
    this.claimSlots = qsa(el, '[data-gp-slots="claim"] .nzs-gp__slot');
    if (this.slots.length > this.cfg.picks) {
      this.slots.splice(this.cfg.picks).concat(this.claimSlots.splice(this.cfg.picks)).forEach(function (slot) { slot.remove(); });
    }
    this.claimHover = null;
    this.barTxt = qs(el, '[data-gp-bar-txt]');
    if (this.barTxt && this.slots.length < parseInt(el.getAttribute('data-picks'), 10)) this.barTxt.textContent = 'Flip ' + this.cfg.picks + ' cards to reveal your gifts';
    this.bar = qs(el, '[data-gp-bar]');
    this.worthLine = qs(el, '[data-gp-worth]');
    this.form = qs(el, 'form.nzs-gp__form');
    this.input = qs(el, '[data-gp-email]');
    this.cta = qs(el, '[data-gp-claim]');
    this.hint = qs(el, '[data-gp-hint]');
    this.bindBody();
  };

  /* NZS-SPD2-0906-T1: the handlers that need the cloned body nodes. Split out of bind() verbatim and
     called from hydrate(), so they are wired the moment the nodes exist and never before. */
  GiftPopup.prototype.bindBody = function () {
    var self = this;

    this.cards.forEach(function (card, i) {
      card.style.setProperty('--i', i);
      on(card, 'click', function () { self.flip(card); });
    });

    qsa(this.el, '[data-gp-close]').forEach(function (b) {
      on(b, 'click', function () { self.close(); });
    });
    on(qs(this.el, '[data-gp-reset]'), 'click', function () {
      self.reset(); self.setView('pick');
    });

    on(this.input, 'input', function () {
      self.el.classList.remove('is-invalid');
      if (self.hint) self.hint.textContent = self.hint.getAttribute('data-default') || '';
    });
    on(this.form, 'submit', function (e) { e.preventDefault(); self.claim(); });
  };

  /* ------------------------------ triggers ----------------------------- */
  GiftPopup.prototype.eligible = function () {
    if (designMode() || forced()) return true;
    var s = readStore();
    if (s.claimed || s.dismissed || s.formDone) return false; // NZS-ROUND4-C-ELIGIBLE: completed form suppresses future prompts.
    if (this.cfg.frequency === 'always') return true;
    if (this.cfg.frequency === 'session') {
      try { return !sessionStorage.getItem(STORE_KEY + '.seen'); } catch (e) { return true; }
    }
    if (!s.seen) return true;
    return (Date.now() - s.seen) > this.cfg.days * 86400000;
  };

  /* NZS-SPD-0820: only 'immediate' and 'delay' remain — the scroll / exit-intent
     branches (and their page-wide listeners) were dead code on every live section. */
  GiftPopup.prototype.schedule = function () {
    if (!this.eligible()) { this.showTab(); return; }

    if (designMode() || forced()) { this.open(); return; }
    if (this.cfg.trigger === 'immediate') { this.open(); return; }

    this.armDelay(this.cfg.delay);
  };

  /* NZS-GP-LOADGATE v2 (2026-08-20): the delay counts from the window `load` event, so the popup
     never races the loading page — and open() refuses to fire while blocked() (skeleton loader /
     overlay / re-loading doc), routing into deferOpen()'s retry instead. v1 was a 250ms poll of
     blocked(); its skeletonVisible() offsetWidth reads forced ~4 layouts/sec through the whole
     LCP window on every page — the event does the same job for free. Page change = full reload
     here (page_transition:false), so the countdown naturally resets on navigation. */
  GiftPopup.prototype.armDelay = function (ms) {
    var self = this;
    var arm = function () { setTimeout(function () { self.open(); }, ms); };
    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
  };

  /* NZS-GP-OVERLAYGATE (Blake 2026-07-31): the popup must never auto-appear on top of
     another background-blurring surface -- mega menu (body.nzs-mega-open), search
     (body.nzs-search-open), the cart drawer, the mobile nav, or anything that has locked
     body scroll (nzsOverlay sets position:fixed; the raw drawer paths set overflow:hidden;
     Alpine's xPopup adds .overflow-hidden). Auto opens defer and quietly retry until the
     page is clear. A manual tab click is user intent and is never gated. */
  function overlayBusy() {
    var b = document.body;
    if (b.classList.contains('nzs-mega-open')
      || b.classList.contains('nzs-search-open')
      || b.classList.contains('overflow-hidden')) return true;
    if (b.style.position === 'fixed' || b.style.overflow === 'hidden') return true;
    return !!document.querySelector('.nzs-cart-drawer.is-open, .nzs-mnav.is-open, .nzs-more.is-open');
  }

  /* NZS-GP-LOADGATE (Blake 2026-08-20): the popup must never auto-appear while the page is
     still loading or a skeleton loader is on screen. Two signals cover it:
       - document.readyState !== 'complete'  -> the document/resources are still loading.
       - a VISIBLE '.nzs-nav-sk' (page-transition overlay from nzs-skeleton.js) or '.nzs-sk'
         (any component skeleton tile) -> a wireframe placeholder is currently showing.
     Visibility is checked (offset boxes / client rects) so a permanently-parked, hidden
     skeleton placeholder can never wedge the gate shut. The delay countdown is gated on this
     too (armDelay), so a page change / skeleton re-appearing RESETS the timer. */
  function skeletonVisible() {
    var els = document.querySelectorAll('.nzs-nav-sk, .nzs-sk');
    for (var i = 0; i < els.length; i++) {
      var e = els[i];
      if (e.offsetWidth || e.offsetHeight || e.getClientRects().length) return true;
    }
    return false;
  }
  function pageBusy() {
    if (document.readyState !== 'complete') return true;
    return skeletonVisible();
  }
  function blocked() { return overlayBusy() || pageBusy(); }

  GiftPopup.prototype.deferOpen = function () {
    var self = this;
    if (this._deferT) return;
    var t0 = Date.now();
    this._deferT = setInterval(function () {
      if (self.phase !== 'idle' || Date.now() - t0 > 60000) { clearInterval(self._deferT); self._deferT = 0; return; }
      if (!blocked()) { clearInterval(self._deferT); self._deferT = 0; self.open(); }
    }, 1500);
  };

  /* -------------------------------- open ------------------------------- */
  GiftPopup.prototype.open = function (manual) {
    injectCss(this.el);   // NZS-SPD-0820: must run BEFORE the position check below
    if (this.phase !== 'idle') return;
    if (!manual && blocked()) { this.deferOpen(); return; }
    if (getComputedStyle(this.el).position !== 'fixed') { this.deferOpen(); return; }
    this.hydrate();   /* NZS-SPD2-0906-T1: body nodes exist from here down (bindClaimHover, this.grid) */
    if (!this._hoverBound) { this._hoverBound = true; this.bindClaimHover(); }
    var self = this;
    this.openedOnce = true;
    this.phase = 'pick';
    this.hideTab();

    /* Sneak peek only ever runs on a clean deck - a re-open with picks kept
       skips it rather than wiping the customer's progress. */
    var peek = this.cfg.peek && !reduced() && this.picked.length === 0;
    if (peek) this.phase = 'peek';

    this.el.classList.add('is-live');
    /* is-peek MUST land before the first reflow below, so the face-up state
       renders instantly instead of running the .62s flip transition. */
    if (peek) this.el.classList.add('is-peek');
    this.lockScroll(true);
    // reflow so the entrance transition actually runs
    void this.el.offsetWidth;
    this.el.classList.add('is-open');
    // re-run the deal stagger every time it opens
    this.el.classList.remove('is-dealt');
    void this.grid.offsetWidth;
    this.el.classList.add('is-dealt');

    if (!manual && !designMode()) {
      writeStore({ seen: Date.now() });
      try { sessionStorage.setItem(STORE_KEY + '.seen', '1'); } catch (e) {}
    }
    if (peek) this.runPeek();
    this.xcur(true);
    /* Focus the DIALOG ROOT, not a card: auto-focusing one card would paint a
       :focus-visible ring on an arbitrary card as the deck deals in, reading as
       "one random card has a different border". The root carries role=dialog +
       aria-label, so AT announces the dialog; real keyboard tabbing still rings
       cards. */
    setTimeout(function () {
      if (self.el.classList.contains('is-open')) self.el.focus({ preventScroll: true });
    }, 620);
  };

  /* Sneak peek: deal in face-up, hold, then wave the cards flip-back to their
     backs. Every stage bails unless we are still in phase 'peek', and every
     timeout id is cleared by close()/destroy() - closing mid-peek must never
     strand a stale tail on a closed popup. */
  GiftPopup.prototype.runPeek = function () {
    var self = this;
    this._peekT = [];
    this._peekT.push(setTimeout(function () {
      if (self.phase !== 'peek') return;
      self.el.classList.add('is-unpeeking');
      self.el.classList.remove('is-peek');
      self._peekT.push(setTimeout(function () {
        if (self.phase !== 'peek') return;
        self.el.classList.remove('is-unpeeking');
        self.phase = 'pick';
      }, 620 + (self.cards.length - 1) * 40 + 60));   // wave budget follows the real deck size
    }, 1125));   // deal-in ~950ms + a short face-up beat; was 1500, cut 25% (Blake 2026-07-30)
  };

  GiftPopup.prototype.clearPeek = function () {
    (this._peekT || []).forEach(clearTimeout);
    this._peekT = [];
    this.el.classList.remove('is-peek', 'is-unpeeking');
  };

  GiftPopup.prototype.xcur = function (on) { var x = window.__nzsX; if (x && this.scrim) x[on ? 'bind' : 'unbind'](this.scrim); };
  GiftPopup.prototype.close = function () {
    var self = this;
    if (this.phase === 'idle') return;
    this.phase = 'idle';
    if (this._deferT) { clearInterval(this._deferT); this._deferT = 0; }
    this.clearPeek();
    this.xcur(false);
    this.el.classList.remove('is-open');
    this.lockScroll(false);
    setTimeout(function () {
      self.el.classList.remove('is-live');
      if (!readStore().claimed) self.showTab();
    }, 420);
  };

  GiftPopup.prototype.lockScroll = function (lock) {
    /* Prefer the shared reference-counted fixed-body lock (nzs-overlay.js): it captures
       and restores the scroll position exactly. Toggling <html> overflow could reset the
       root scroller on some mobile browsers (the intermittent mid-page jump-to-top), so
       only fall back to it if the shared lock isn't present. */
    var ov = window.nzsOverlay;
    if (ov && ov.acquireScrollLock && ov.releaseScrollLock) {
      if (lock === !!this._overlayLocked) return;
      this._overlayLocked = lock;
      (lock ? ov.acquireScrollLock : ov.releaseScrollLock)();
      return;
    }
    var html = document.documentElement;
    if (lock) {
      this._prevOverflow = html.style.overflow;
      html.style.overflow = 'hidden';
    } else {
      html.style.overflow = this._prevOverflow || '';
    }
  };

  GiftPopup.prototype.showTab = function () {
    /* NZS-GP-QTY: claimed visitors are done -- the tab only nags people who
       closed without claiming. close() already checked this; eligible()'s
       showTab() path did not, so it is enforced here for both callers. */
    var st = readStore();
    if (this.cfg.showTab && this.dock && !st.dismissed && !st.claimed && !st.formDone) {
      injectCss(this.el);   // NZS-SPD-0820: the dock is hidden by the inline block until this lands
      this.dock.classList.add('is-on'); // NZS-ROUND4-C-TAB
    }
  };
  GiftPopup.prototype.hideTab = function () { if (this.dock) this.dock.classList.remove('is-on'); };

  /* -------------------------------- flip ------------------------------- */
  GiftPopup.prototype.flip = function (card) {
    if (this.phase !== 'pick') return;
    var self = this;
    var idx = this.picked.indexOf(card);

    if (idx > -1) {                       // un-pick
      this.picked.splice(idx, 1);
      card.classList.remove('is-flipped');
      card.setAttribute('aria-pressed', 'false');
      this.el.classList.remove('is-locked');
      clearTimeout(this.advance);
      this.render();
      return;
    }
    if (this.picked.length >= this.cfg.picks) return;

    this.picked.push(card);
    card.classList.add('is-flipped');
    card.setAttribute('aria-pressed', 'true');
    this.render();

    if (this.picked.length >= this.cfg.picks) {
      this.el.classList.add('is-locked');
      clearTimeout(this.advance);
      this.advance = setTimeout(function () {
        if (self.picked.length >= self.cfg.picks) self.setView('email');
      }, 950);
    }
  };

  GiftPopup.prototype.bindClaimHover = function () {
    var self = this;
    var fineHover = window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches;
    if (!fineHover || !this.claimSlots.length) return;

    var layer = document.createElement('aside');
    layer.className = 'nzs-gp-hover';
    layer.setAttribute('aria-hidden', 'true');

    var imageBox = document.createElement('span');
    imageBox.className = 'nzs-gp-hover__img';
    var image = document.createElement('img');
    image.alt = '';
    imageBox.appendChild(image);

    var body = document.createElement('span');
    body.className = 'nzs-gp-hover__b';
    var title = document.createElement('span');
    title.className = 'nzs-gp-hover__t';
    var price = document.createElement('span');
    price.className = 'nzs-gp-hover__p';
    var oldPrice = document.createElement('s');
    oldPrice.className = 'nzs-gp-hover__old';
    var free = document.createElement('strong');
    free.textContent = 'FREE';
    price.appendChild(oldPrice);
    price.appendChild(free);
    var hint = document.createElement('span');
    hint.className = 'nzs-gp-hover__hint';
    body.appendChild(title);
    body.appendChild(price);
    body.appendChild(hint);
    layer.appendChild(imageBox);
    layer.appendChild(body);
    document.body.appendChild(layer);
    this.claimHover = layer;

    var hide = function () {
      layer.classList.remove('is-in');
    };
    this.claimSlots.forEach(function (slot, i) {
      var show = function () {
        var card = self.picked[i];
        if (!card || card.getAttribute('data-perk') === 'true') {
          hide();
          return;
        }
        // NZS-SPD-0820: read the card's own thumbnail instead of a duplicate data-img URL
        var imgEl = card.querySelector('.nzs-gp__img');
        var imageUrl = imgEl ? imgEl.currentSrc || imgEl.src : '';
        var cents = parseInt(card.getAttribute('data-price'), 10);
        title.textContent = card.getAttribute('data-title') || '';
        var _short = card.getAttribute('data-short') || '';
        hint.textContent = _short || 'Gift locked in';
        image.src = imageUrl;
        imageBox.hidden = !imageUrl;
        oldPrice.textContent = isNaN(cents) ? '' : money(cents, self.cfg.symbol);
        oldPrice.hidden = isNaN(cents);

        layer.classList.remove('is-in');
        var rect = slot.getBoundingClientRect();
        var width = layer.offsetWidth || 230;
        var height = layer.offsetHeight || 96;
        var left = Math.max(8, Math.min(window.innerWidth - width - 8, rect.left + rect.width / 2 - width / 2));
        var top = rect.top - height - 10;
        if (top < 8) top = Math.min(window.innerHeight - height - 8, rect.bottom + 10);
        layer.style.left = Math.round(left) + 'px';
        layer.style.top = Math.round(Math.max(8, top)) + 'px';
        layer.classList.add('is-in');
      };
      on(slot, 'mouseenter', show);
      on(slot, 'mouseleave', hide);
    });
  };

  /* ponytail: the slot handlers are left bound — the layer they write to is gone, and a
     re-init binds fresh handlers onto a fresh layer, so tracking them earned nothing. */
  GiftPopup.prototype.destroyClaimHover = function () {
    if (this.claimHover) {
      this.claimHover.remove();
      this.claimHover = null;
    }
  };

  /* ------------------------------- render ------------------------------ */
  GiftPopup.prototype.fillSlot = function (slot, card) {
    slot.textContent = '';
    if (!card) { slot.classList.remove('is-full'); return; }
    slot.classList.add('is-full');
    // NZS-SPD-0820: the card's own face image is the source of truth (was a duplicate data-img)
    var imgEl = card.querySelector('.nzs-gp__img');
    var img = imgEl ? imgEl.currentSrc || imgEl.src : '';
    if (img) {
      var im = document.createElement('img');
      im.src = img;
      im.alt = '';
      slot.appendChild(im);
    } else {
      var sp = document.createElement('span');
      sp.className = 'nzs-gp__slot-txt';
      sp.textContent = card.getAttribute('data-short') || card.getAttribute('data-title') || '';
      slot.appendChild(sp);
    }
  };

  GiftPopup.prototype.render = function () {
    var self = this;
    var left = this.cfg.picks - this.picked.length;

    [this.slots, this.claimSlots].forEach(function (group) {
      group.forEach(function (slot, i) { self.fillSlot(slot, self.picked[i]); });
    });

    if (this.barTxt) {
      this.barTxt.textContent =
        left === this.cfg.picks ? 'Flip ' + this.cfg.picks + ' cards to reveal your gifts'
        : left > 0 ? (left === 1 ? 'Nice pick — one more to go' : 'Nice pick — flip ' + left + ' more')
        : 'Gifts locked in';
    }
    if (this.bar && this.picked.length) {
      this.bar.classList.remove('is-beat');
      void this.bar.offsetWidth;
      this.bar.classList.add('is-beat');
    }

    var cents = this.picked.reduce(function (sum, c) {
      return sum + (parseInt(c.getAttribute('data-price'), 10) || 0);
    }, 0);
    if (this.worthLine) {
      this.worthLine.textContent = cents > 0 ? "You're claiming " + money(cents, this.cfg.symbol) + ' of gifts — free' : '';
      this.worthLine.style.display = cents > 0 ? '' : 'none';
    }
  };

  GiftPopup.prototype.setView = function (name) {
    var self = this;
    var views = qsa(this.el, '.nzs-gp__view');
    var current = views.filter(function (v) { return v.classList.contains('is-on'); })[0];
    var target = views.filter(function (v) { return v.getAttribute('data-gp-view') === name; })[0];

    /* phase flips SYNCHRONOUSLY so flip() stops accepting clicks the moment
       the pick view starts fading out */
    this.phase = name === 'email' ? 'email' : name === 'done' ? 'done' : 'pick';
    if (name === 'pick') this.el.classList.remove('is-locked');

    var swap = function () {
      views.forEach(function (v) {
        v.classList.toggle('is-on', v.getAttribute('data-gp-view') === name);
      });
      if (name === 'email' && self.input && !reduced()) {
        setTimeout(function () { self.input.focus({ preventScroll: true }); }, 280);
      }
    };

    var modal = qs(this.el, '.nzs-gp__modal');
    var canAnim = !reduced() && modal && modal.animate &&
      this.el.classList.contains('is-open') && current && target && current !== target;
    if (!canAnim) { swap(); return; }

    if (this._viewBusy) { this._viewQueued = name; return; }   // finishes, then chains
    this._viewBusy = true;

    var h0 = modal.offsetHeight;
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      self._viewBusy = false;
      var q = self._viewQueued;
      self._viewQueued = null;
      if (q && q !== name) self.setView(q);
    };

    /* fill:'forwards' avoids a one-frame snap-back before the swap - but a
       forwards-filled SCRIPT animation outranks the CSS nzsGpViewIn forever,
       so it MUST be cancelled right after the class swap. */
    var out = current.animate(
      [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-14px)' }],
      { duration: 180, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );
    this._viewAnim = out;
    var proceeded = false;
    var proceed = function () {
      if (proceeded) return;
      proceeded = true;
      try { out.cancel(); } catch (e) {}
      self._viewAnim = null;
      swap();
      var h1 = modal.offsetHeight;
      if (h1 !== h0) {
        var m = modal.animate(
          [{ height: h0 + 'px' }, { height: h1 + 'px' }],
          { duration: 320, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
        );
        if (m.finished && m.finished.then) m.finished.then(finish).catch(finish);
        m.onfinish = finish;
        setTimeout(finish, 480);
      } else {
        finish();
      }
    };
    if (out.finished && out.finished.then) out.finished.then(proceed).catch(proceed);
    out.onfinish = proceed;
    setTimeout(proceed, 300);
  };

  GiftPopup.prototype.reset = function () {
    this.clearPeek();                        // a reset mid-peek must not strand face-up cards
    clearTimeout(this.advance);
    this.picked = [];
    this.cards.forEach(function (c) { c.classList.remove('is-flipped'); c.setAttribute('aria-pressed', 'false'); });
    this.el.classList.remove('is-locked', 'is-invalid');
    this.render();
    this.phase = 'pick';
  };

  /* -------------------------------- claim ------------------------------ */
  GiftPopup.prototype.claim = function () {
    var self = this;
    if (this.phase === 'claiming') return;

    var email = (this.input && this.input.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      this.el.classList.add('is-invalid');
      if (this.hint) this.hint.textContent = 'Enter a valid email address to claim your gifts.';
      if (this.input) this.input.focus();
      return;
    }

    this.phase = 'claiming';
    writeStore({ formDone: Date.now() }); // NZS-ROUND4-C-FORMDONE: valid submission completes the prompt.
    if (this.cta) {
      this.cta.disabled = true;
      // ponytail: the completed claim flow never restores this label.
      this.cta.textContent = 'Locking in your gifts…';
    }

    this.subscribe(email);

    /* One continuous sequence:
         modal recedes  ->  cards arc out and HIT the cart icon (bounce per hit)
         ->  short beat  ->  cart drawer slides open.
       The cart request runs underneath it, capped so a slow response can
       never leave a dead pause at the end of the animation. */
    var still = reduced();
    /* Await the REAL add — never a timeout race. This theme has no event-driven
       cart refresh, so OPENING the drawer is what fetches /cart.js. Open before
       the add lands and the drawer renders without the gifts, with nothing left
       to re-render it. (This was withTimeout(addGifts(), 1400); the slower theme
       -editor preview lost that race routinely.) */
    var cartReq = this.addGifts();
    var flight = this.flyToCart();
    if (still) { this.setView('done'); this.phase = 'claiming'; } else { this.recede(); }
    /* NZS-SPEED-LDR-GP removed (Blake 2026-07-31): no dots under the cart icon while
       the gifts land - the staggered flight + icon bounce is the feedback. aria-busy kept. */
    var pendingHost = document.getElementById('nzsCartToggle');
    if (pendingHost) pendingHost.setAttribute('aria-busy', 'true');

    Promise.all([cartReq, flight]).then(function () {
      writeStore({ claimed: true, claimedAt: Date.now(), email: email });
      setTimeout(function () {
        self.finish();
        /* The ONLY working drawer refresh in this theme. A synthetic click is
           isTrusted:false, so nzs-header's handler always routes to open(), and
           open() unconditionally calls fetchCart() -> GET /cart.js -> render().
           cart:refresh / cart:build / cart:updated have ZERO listeners anywhere —
           they were dispatched here and did nothing. Do not add them back. */
        var btn = cartButton();
        if (btn) btn.click();
        /* NZS-GP-STICKYOPEN (Blake 2026-07-31): something outside this file (an app's cart
           rebuild) was closing the freshly opened drawer. For ~3s after the claim, a close
           that was NOT preceded by a real user gesture is undone; the first trusted
           pointer/key event stands down the guard so the customer can close it freely. */
        if (btn) {
          var userActed = false;
          var mark = function (e) { if (e.isTrusted) userActed = true; };
          document.addEventListener('pointerdown', mark, true);
          document.addEventListener('keydown', mark, true);
          var t0 = Date.now();
          var hold = setInterval(function () {
            if (userActed || Date.now() - t0 > 3200) {
              clearInterval(hold);
              document.removeEventListener('pointerdown', mark, true);
              document.removeEventListener('keydown', mark, true);
              return;
            }
            var d = document.querySelector('.nzs-cart-drawer');
            if (d && !d.classList.contains('is-open')) btn.click();
          }, 350);
        }
      }, still ? 900 : 240);
    }).catch(function (err) {
      self.phase = 'form';
      if (self.cta) { self.cta.disabled = false; self.cta.textContent = 'Try again'; }
      if (self.hint) self.hint.textContent = (err && err.message) || "Couldn't add gifts";
      if(!window.nzsATC) document.dispatchEvent(new CustomEvent('nzs:cart-error',{detail:{message:(err&&err.message)||"Couldn't add gifts"}}));
    }).finally(function () {
      if (pendingHost) pendingHost.removeAttribute('aria-busy');
    });
  };

  /* Push the picked gifts into the cart, then set the discount cookie. */
  GiftPopup.prototype.addGifts = function () {
    var self = this;
    var items = [], handles = [];
    if (this.cfg.addToCart) this.picked.forEach(function (c) {
      var vid = c.getAttribute('data-vid');
      if (!vid) return;                            // perk card — nothing real to add
      /* NZS-GP-QTY: a card can be worth more than one unit (data-qty on the card). */
      var qty = parseInt(c.getAttribute('data-qty'), 10) || 1;
      items.push({ id: parseInt(vid, 10), quantity: qty, properties: { _nzs_gift: 'pick-a-card' } });
      handles.push(c.getAttribute('data-handle') || '');
    });

    var add = items.length
      ? (window.nzsATC
          ? window.nzsATC.add({ items: items }, { handles: handles })
          : Promise.reject(new Error("Couldn't add gifts")))
      : Promise.resolve(null);

    /* PERK CODES: Shopify checkout only holds a single discount cookie, so if
       more than one picked card carries a code, the LAST one picked wins. */
    var perkCode = this.picked.reduce(function (found, c) {
      var code = (c.getAttribute('data-code') || '').trim();
      return code || found;
    }, '');

    return add
      .then(function () {
        if (!self.cfg.discount) return null;
        // hitting /discount/CODE sets the discount cookie for checkout
        return fetch(root_() + 'discount/' + encodeURIComponent(self.cfg.discount), { credentials: 'same-origin' })
          .catch(function () { return null; });
      })
      .then(function () {
        if (!perkCode) return null;
        /* NZS-GP-HIDDENPERK (Blake 2026-07-31): PICKED2.5 is not a checkout code — we stamp
           the cart with `_EmailPerk` and nzs-stack-lockdown's ORDER branch pays it out as a
           hidden automatic discount. NZS-SPD-0820: inlined, it was a one-entry map. */
        if (perkCode.toUpperCase() === 'PICKED2.5') {
          return fetch(root_() + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { _EmailPerk: 'pick-a-card' } })
          }).catch(function () { return null; });
        }
        // a block-level perk code wins over the section-level discount above —
        // fetched last, so it is the one left in the checkout cookie
        return fetch(root_() + 'discount/' + encodeURIComponent(perkCode), { credentials: 'same-origin' })
          .catch(function () { return null; });
      })
      .catch(function (err) {
        if (window.console) console.warn('[nzs-gift-popup] cart add failed', err);
        throw err;
      });
  };

  /* Modal steps back as the cards leave, so they fly over a clean page and
     the cart icon is never dimmed by the scrim when it gets hit. */
  GiftPopup.prototype.recede = function () {
    var self = this;
    this.xcur(false);
    this.el.classList.add('is-flying');
    this.el.classList.remove('is-open');
    this.lockScroll(false);
    setTimeout(function () { self.el.classList.remove('is-live'); }, 460);
  };

  GiftPopup.prototype.finish = function () {
    this.phase = 'idle';
    if (this._deferT) { clearInterval(this._deferT); this._deferT = 0; }
    this.xcur(false);
    this.el.classList.remove('is-live', 'is-open', 'is-flying');
    this.hideTab();
  };

  /* -------------------------- email opt-in ----------------------------- */
  GiftPopup.prototype.subscribe = function (email) {
    var jobs = [];
    if (this.cfg.klaviyoKey && this.cfg.klaviyoList) jobs.push(this.klaviyo(email));
    if (this.cfg.shopifyCustomer) jobs.push(this.shopifyCustomer());
    return Promise.all(jobs);
  };

  /* Klaviyo client-side subscription (public key + list id — both safe in a
     theme). Double opt-in lists still send their confirmation email. */
  GiftPopup.prototype.klaviyo = function (email) {
    var gifts = this.picked.map(function (c) { return c.getAttribute('data-title'); });
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.cfg.klaviyoSource || 'Gift popup',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: {
                  'Gift popup gifts': gifts,
                  'Gift popup page': window.location.pathname
                }
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: this.cfg.klaviyoList } } }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.cfg.klaviyoKey), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(function (e) {
      if (window.console) console.warn('[nzs-gift-popup] klaviyo subscribe failed', e);
      return null;
    });
  };

  /* Native Shopify customer form — creates/tags the customer alongside Klaviyo. */
  GiftPopup.prototype.shopifyCustomer = function () {
    if (!this.form) return Promise.resolve(null);
    try {
      return fetch(this.form.getAttribute('action') || '/contact', {
        method: 'POST',
        body: new FormData(this.form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      }).catch(function () { return null; });      // best-effort; never block the gift
    } catch (e) { return Promise.resolve(null); }
  };

  /* ---------------------- fly the gifts into the cart ------------------- */
  /* Resolves once every card has landed, so the drawer can wait for it. */
  GiftPopup.prototype.flyToCart = function () {
    var self = this;
    var target = cartButton();
    var sources = this.claimSlots.filter(function (s) {
      return s.classList.contains('is-full') && s.getBoundingClientRect().width;
    });
    if (reduced() || !sources.length || typeof Element === 'undefined' || !Element.prototype.animate) {
      if (target) this.hitCart(target);
      return Promise.resolve();
    }

    var tr = target
      ? target.getBoundingClientRect()
      : { left: window.innerWidth - 56, top: 24, width: 32, height: 32 };

    return Promise.all(sources.map(function (src, i) {
      return new Promise(function (resolve) {
        var r = src.getBoundingClientRect();

        var fly = src.cloneNode(true);
        fly.className = 'nzs-gp-fly';
        fly.style.left = r.left + 'px';
        fly.style.top = r.top + 'px';
        fly.style.width = r.width + 'px';
        fly.style.height = r.height + 'px';
        document.body.appendChild(fly);

        var dx = (tr.left + tr.width / 2) - (r.left + r.width / 2);
        var dy = (tr.top + tr.height / 2) - (r.top + r.height / 2);
        var arc = Math.min(150, Math.abs(dy) * .34 + 70);   // lob it, don't drag it
        var delay = 90 + i * 160;
        var dur = 780;
        var impact = .88;                                   // when it reaches the icon

        var anim = fly.animate(
          [
            { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
            { transform: 'translate(' + (dx * .5) + 'px,' + (dy * .5 - arc) + 'px) scale(.66) rotate(7deg)', opacity: 1, offset: .55 },
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.26) rotate(12deg)', opacity: 1, offset: impact },
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.08) rotate(14deg)', opacity: 0, offset: 1 }
          ],
          { duration: dur, delay: delay, easing: 'cubic-bezier(.4,.02,.32,1)', fill: 'forwards' }
        );

        // bounce the icon at the moment of contact, not when the tween ends
        setTimeout(function () { self.hitCart(target); }, delay + dur * impact);

        var landed = false;
        var land = function () {
          if (landed) return;
          landed = true;
          fly.remove();
          resolve();
        };
        if (anim.finished && anim.finished.then) anim.finished.then(land).catch(land);
        anim.onfinish = land;
        setTimeout(land, delay + dur + 160);               // safety net if WAAPI is throttled
      });
    }));
  };

  /* The hit: squash-and-settle bounce on the cart icon + an impact ring. */
  GiftPopup.prototype.hitCart = function (target) {
    if (!target) return;
    if (target.animate && !reduced()) {
      target.animate(
        [
          { transform: 'scale(1)', offset: 0 },
          { transform: 'scale(1.34)', offset: .26 },
          { transform: 'scale(.9)', offset: .52 },
          { transform: 'scale(1.12)', offset: .76 },
          { transform: 'scale(1)', offset: 1 }
        ],
        { duration: 480, easing: 'cubic-bezier(.22, 1, .36, 1)' }
      );
    }
    if (reduced()) return;
    var r = target.getBoundingClientRect();
    var ring = document.createElement('span');
    ring.className = 'nzs-gp-pulse';
    ring.style.left = (r.left + r.width / 2) + 'px';
    ring.style.top = (r.top + r.height / 2) + 'px';
    document.body.appendChild(ring);
    setTimeout(function () { ring.remove(); }, 700);
  };

  GiftPopup.prototype.destroy = function () {
    if (this._deferT) { clearInterval(this._deferT); this._deferT = 0; }
    this.destroyClaimHover();
    this.clearPeek();
    if (this._viewAnim) { try { this._viewAnim.cancel(); } catch (e) {} }
    this.xcur(false);
    clearTimeout(this.advance);
    document.removeEventListener('keydown', this._esc);
    this.lockScroll(false);
  };

  /* -------------------------------- init ------------------------------- */
  function init() {
    instances.forEach(function (i) { i.destroy(); });
    instances = qsa(document, '[data-nzs-gp]').map(function (el) { return new GiftPopup(el); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-boot whenever the section is re-rendered or selected.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-nzs-gp]')) init();
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (!e.target || !e.target.querySelector('[data-nzs-gp]')) return;
    instances.forEach(function (i) { i.open(true); });
  });
})();
